import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import {
  EXPERIENCE_TYPE_KITCHEN,
  EXPERIENCE_TYPE_LAUNDRY,
  DRYER,
  WASHER,
  EXPERIENCE_TYPE_GM,
  BUNDLE_TYPE_PACKAGE,
  NONE_VALUE
} from '../constants/index';
import { ENTRY_POINT_CONFIGURATION } from '../constants/entry-point-configuration';
import { hasArrayValues } from './data';

export const getUrlSearchParams = (param, queryString) => {
  const searchParam = new RegExp('[?&]' + param + '=([^&#]*)').exec(queryString);
  return searchParam && decodeURI(searchParam[1]);
};

export const shouldDisplayReferrerRecommendations = (referer) => {
  return /google.com|bing.com|yahoo.com|duckduckgo.com|aol.com/i.test(referer)
    || /^Shopping/.test(getUrlSearchParams('cm_mmc', referer))
    || /PIN_Shopping/.test(getUrlSearchParams('cm_mmc', referer));
};

export const shouldDisplayPipInStock = (data = {}, referer) => {
  const { product = {} } = data || {};
  if (shouldDisplayReferrerRecommendations(referer)) return false;
  if (podFulFillmentUtils.isDiscontinued(product)) return true;
  if (product?.availabilityType?.type === 'Browse Only') return podFulFillmentUtils.isOutOfStockYourStore(product);
  if (product?.availabilityType?.type === 'Online') return podFulFillmentUtils.isOutOfStockOnline(product);
  if (product?.availabilityType?.type === 'Shared') {
    return !!(podFulFillmentUtils.isOutOfStockOnline(product)
      || podFulFillmentUtils.isOutOfStockShared(product)
      || podFulFillmentUtils.isLimitedStock(product)
      || podFulFillmentUtils.isBackordered(product)
      || !podFulFillmentUtils.isATCEnabled(product));
  }
  if (product?.availabilityType?.type === 'Store Only') {
    return podFulFillmentUtils.isBuyInStore(product);
  }
  return false;
};

export const shouldDisplayRecsFBT = (data = {}, referer = false) => {
  return data?.product?.info?.recommendationFlags?.frequentlyBoughtTogether && !shouldDisplayPipInStock(data, referer);
};

export const shouldDisplayFbtbundle = (data = {}) => {
  return data?.product?.info?.recommendationFlags?.bundles;
};

export const shouldDisplayFBTPMV = (setIsPMVEnabled, setIsFBTEnabled, setIsPMVAndFBTEnabled) => {
  LIFE_CYCLE_EVENT_BUS.lifeCycle.on('enablePackagesMiniVisual', (data) => {
    setIsPMVEnabled(data?.output?.enabled || false);
  });
  LIFE_CYCLE_EVENT_BUS.lifeCycle.on('enableFrequentlyBoughtTogether', (data) => {
    setIsFBTEnabled(data?.output?.enabled || false);
  });
  LIFE_CYCLE_EVENT_BUS.lifeCycle.on('enablePMVAndFBT', (data) => {
    setIsPMVAndFBTEnabled(data?.output?.enabled || false);
  });
};

const isDiscontinued = (product) => {
  return !!(product?.availabilityType?.discontinued);
};

const isHidden = (product) => {
  return product?.identifiers?.hideItem === 'true' || product?.identifiers?.hideItem === true;
};

const getNearByStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  if (locations.length > 1) {
    const nearbyStore = locations.find((location) => !location.isAnchor);
    return nearbyStore;
  }
  return null;
};
const getLocalStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  const localStore = locations.find((location) => location.isAnchor);
  return localStore;
};

const isBOPISHaveQuantity = (product) => {
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');
  if (!bopisFulfillment) {
    return false;
  }
  const nearByStoreLocation = getNearByStore(bopisFulfillment);
  const localStoreLocation = getLocalStore(bopisFulfillment);
  return pickupService?.fulfillable
    && (localStoreLocation?.inventory?.quantity > 0 || nearByStoreLocation?.inventory?.quantity > 0);
};

export const isDisplayableBasedOnFulfillment = (product) => {
  const deliveryService = (product?.fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'delivery');
  const pickupService = (product?.fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');
  const bossFulfillment = (pickupService?.services || []).find((service) => service.type === 'boss');
  return !!(!isDiscontinued(product)
    && (deliveryService
      || shippingFulfillment
      || bossFulfillment
      || isBOPISHaveQuantity(product)));
};

export const isPackageRecommendationFlag = (data = {}) => {
  return !!data?.info?.recommendationFlags?.packages;
};

export const isProductAvailable = (product, abcTestEnable) => {
  if (
    !product
    || (abcTestEnable && isPackageRecommendationFlag(product) && !isDisplayableBasedOnFulfillment(product))
  ) {
    return null;
  }
  return product;
};

export const shouldDisplayConfigurator = (data = {}, isServer) => {
  return (
    !isServer
    && data?.product?.identifiers?.productType === 'CONFIGURABLE_BLINDS'
  );
};

export const getBundleImage = (parent, sizeIndex = null) => {
  if (!parent?.media?.images) {
    return '';
  }
  const { sizes, url } = parent?.media?.images?.[0] || {};
  const index = sizeIndex || sizes?.length - 1;
  const size = sizes?.[index] || '';

  return url?.replace('<SIZE>', size);
};

export const getButtonText = (experienceType) => {
  switch (experienceType) {
  case EXPERIENCE_TYPE_KITCHEN:
    return 'View Package';
  case EXPERIENCE_TYPE_GM:
    return 'View Bundle';
  case EXPERIENCE_TYPE_LAUNDRY:
    return 'View Set';
  default:
    return 'View Bundle';
  }
};

export const getBrandName = (product, products) => {
  const { brand } = product?.bundleSpecificationDetails ?? {};
  if (brand) {
    return brand;
  }

  const { brandName: productBrandName } = product?.identifiers ?? {};
  if (productBrandName) {
    return productBrandName;
  }

  const { brandName: bundleBrandName } = products?.[0]?.identifiers ?? {};
  const showBrandName = products?.every((bundleProduct) => bundleProduct?.identifiers?.brandName === bundleBrandName);
  return showBrandName ? bundleBrandName : null;
};

export const filterHiddenDiscontinuedProducts = (products) => {
  return products?.filter((product) => {
    return !isHidden(product) && !isDiscontinued(product);
  });
};

export const tiggerAnalyticsCustomEvent = (eventName, data) => {
  if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
    window.LIFE_CYCLE_EVENT_BUS.trigger(eventName, data);
  }
};

const getExperienceType = (product) => {
  return product?.bundleSpecificationDetails?.experienceType || EXPERIENCE_TYPE_GM;
};

export const getTotalPriceText = (products, features) => {
  const text = products?.length > 1 ? (features?.totalPriceText)
    : (features?.singleTotalPriceText);
  return text;
};

export const formatValuesIntoArray = (value) => value?.replace(/,\s*$/, '').split(',');

export const getUpdatedLaundryBundle = (bundle, isAccessoriesFeatureEnabled) => {
  const newBundleSpecs = { ...bundle?.bundleSpecificationDetails };
  if (!isAccessoriesFeatureEnabled) {
    newBundleSpecs.accessories = [];
  }

  return {
    ...bundle,
    bundleSpecificationDetails: {
      ...newBundleSpecs,
      experienceType: EXPERIENCE_TYPE_LAUNDRY,
      type: BUNDLE_TYPE_PACKAGE
    }
  };
};

const sortProductsByCategory = (products) => {
  const order = { [WASHER]: 0, [DRYER]: 1 };

  return products.slice().sort((product1, product2) => {
    const p1 = order[product1?.identifiers?.applianceType] ?? 2;
    const p2 = order[product2?.identifiers?.applianceType] ?? 2;
    return p1 - p2;
  });
};

export const getAccessoryQuantity = (bundleDetails, productId, offeringName) => {
  if (!productId || !offeringName || !bundleDetails) {
    return 1;
  }
  const productData = bundleDetails?.products?.[productId]?.offerings?.find(
    (offering) => offering.offeringName === offeringName
  );

  return productData?.quantity || 1;
};

export const getProductQuantity = (productId, bundleDetails) => {
  if (!productId || !bundleDetails) {
    return 1;
  }

  const productData = bundleDetails?.products?.[productId];
  return productData?.quantity || 1;
};

export const updateLaundryProducts = ({ products, bundleDetails, selectedAccessory }) => {
  if (!hasArrayValues(products) || !bundleDetails) {
    return [];
  }

  const features = {
    directDeliveryCartMethod: true
  };

  return products.map((product) => {
    const { isAccessory } = bundleDetails?.products?.[product.itemId] || {};

    if (isAccessory) {
      const defaultOfferingName = bundleDetails?.variants?.accessories?.[0]?.value;
      const quantity = getAccessoryQuantity(bundleDetails, product?.itemId, selectedAccessory || defaultOfferingName);
      return {
        ...product,
        quantity,
        features
      };
    }

    const quantity = getProductQuantity(product?.itemId, bundleDetails);
    return {
      ...product,
      quantity,
      features,
      selectedVariant: product?.itemId,
      selectableVariants: bundleDetails?.variants?.products?.[product.itemId] || []
    };
  });
};

export const groupLaundryProductsByType = (productsData, bundleDetails) => {
  if (!hasArrayValues(productsData) || !bundleDetails) {
    return {
      products: null,
      accessories: null
    };
  }

  const products = [];
  const accessories = {
    variants: [],
    products: [],
    selectedVariant: NONE_VALUE
  };

  const { products: detailProducts, variants } = bundleDetails;

  accessories.variants = variants.accessories || [];

  productsData.forEach((product) => {
    const details = detailProducts[product.itemId] || {};
    if (details?.isAccessory) {
      accessories.products.push(product);
    } else {
      products.push(product);
    }
  });

  return {
    accessories,
    products: sortProductsByCategory(products)
  };
};

export const getValidAccessoryOfferings = (offerings) => {
  if (!hasArrayValues(offerings)) return null;

  const filteredOfferings = offerings?.reduce((acc, offering) => {
    const isValidOffering = hasArrayValues(offering?.products);
    if (!isValidOffering) return acc;
    if (offering?.products?.length !== 1) return acc;

    const validProducts = offering?.products?.filter((product) => !!product?.defaultProductId);
    if (!hasArrayValues(validProducts)) return acc;

    return [
      ...acc,
      {
        ...offering,
        products: validProducts
      }
    ];
  }, []);

  if (!hasArrayValues(filteredOfferings)) return null;

  const sortedOfferings = filteredOfferings?.sort((accessory1, accessory2) => {
    const accessory1Priority = accessory1?.priority || 1;
    const accessory2Priority = accessory2?.priority || 1;
    return accessory1Priority - accessory2Priority;
  });

  return sortedOfferings.map((offering) => ({
    ...offering,
    products: offering.products.map((product) => ({
      ...product,
      quantity: product?.quantity || 1
    }))
  }));
};

export const getBundleAccessories = (bundleDetails) => {
  const categories = bundleDetails?.accessories?.categories;
  if (!hasArrayValues(categories)) {
    return null;
  }

  const accessoryOfferings = categories[0]?.offerings;

  if (!hasArrayValues(accessoryOfferings)) {
    return null;
  }

  return getValidAccessoryOfferings(accessoryOfferings);
};

const processBundleComponents = (components) => {
  const componentProducts = {};
  const componentVariants = {};

  (components || []).forEach((component) => {
    componentProducts[component.defaultProductId] = {
      quantity: component?.quantity || 1,
      isAccessory: false
    };
    (component?.selectableVariants || []).forEach((variant) => {
      componentProducts[variant.id] = {
        quantity: component?.quantity || 1,
        isAccessory: false
      };
      componentVariants[variant.id] = component?.selectableVariants?.map((variantOption) => ({
        name: variantOption.value,
        value: variantOption.id
      }));
    });
  });

  return {
    componentProducts,
    componentVariants
  };
};

export const processBundleAccessories = (accessoryOfferings) => {
  const accessoryProducts = {};

  accessoryOfferings.forEach((offering) => {
    offering.products.forEach((product) => {
      const productId = product.defaultProductId;

      if (accessoryProducts[productId]) {
        if (!accessoryProducts[productId].offerings) {
          accessoryProducts[productId].offerings = [];
        }
        accessoryProducts[productId].offerings.push({
          offeringName: offering.offeringName,
          quantity: product?.quantity || 1
        });
      } else {
        accessoryProducts[productId] = {
          quantity: product?.quantity || 1,
          isAccessory: true,
          offerings: [{
            offeringName: offering.offeringName,
            quantity: product?.quantity || 1
          }]
        };
      }
    });
  });

  const accessoryVariants = accessoryOfferings.map((offering) => ({
    name: offering.offeringName,
    value: offering.offeringName
  }));

  accessoryVariants.push({
    name: NONE_VALUE,
    value: NONE_VALUE
  });

  return {
    accessoryProducts,
    accessoryVariants
  };
};

export const getBundleDetails = (bundleSpecifications) => {
  let products = {};
  let variants = {
    products: {},
    accessories: {}
  };
  const { componentProducts, componentVariants } = processBundleComponents(bundleSpecifications?.components);
  products = {
    ...products,
    ...componentProducts
  };
  variants = {
    ...variants,
    products: componentVariants
  };

  const accessoryOfferings = getBundleAccessories(bundleSpecifications);

  if (hasArrayValues(accessoryOfferings)) {
    const { accessoryProducts, accessoryVariants } = processBundleAccessories(accessoryOfferings);
    products = {
      ...products,
      ...accessoryProducts
    };
    variants = {
      ...variants,
      accessories: accessoryVariants
    };
  }

  return { products, variants };
};

export const getAccessoryProductIds = (offerings) => {
  if (!hasArrayValues(offerings)) {
    return [];
  }

  const productIds = offerings.flatMap((offering) => offering.products.map((product) => product.defaultProductId));
  return [...new Set(productIds)];
};

export const getBundleFeatures = (bundleType, experienceType) => {
  if (!experienceType || !bundleType) {
    return {};
  }

  return ENTRY_POINT_CONFIGURATION?.[bundleType]?.[experienceType] || {};
};

const getBundleType = (bundle) => bundle?.bundleSpecificationDetails?.type;

export const getUpdatedBundle = (bundle, configExperienceType, configBundleType) => {
  const experienceType = configExperienceType || getExperienceType(bundle);
  const bundleType = configBundleType || getBundleType(bundle);
  return {
    ...bundle,
    features: getBundleFeatures(bundleType, experienceType)
  };
};

export const getUpdatedProducts = (products, bundleComponents, configBundleProductsDetails) => {
  return products?.map((product) => {
    const filteredProduct = bundleComponents.find((component) => product?.itemId === component?.defaultProductId);
    return {
      ...product,
      quantity: Number(configBundleProductsDetails?.[product?.itemId]?.quantity)
        || filteredProduct?.quantity || 1
    };
  });
};

export const getTotalItems = (products) =>
  products?.reduce((total, product) => total + (product?.quantity || 1), 0);

export const getLaundryDefaultProductIds = ({ components, accessories }) => {
  const componentProductIds = components?.map(
    ({ defaultProductId }) => defaultProductId
  ) || [];

  const accessoryProductIds = getAccessoryProductIds(accessories);

  const initialAccessoryIds = accessoryProductIds.length > 0 ? [accessoryProductIds[0]] : [];
  return [...componentProductIds, ...initialAccessoryIds];
};

export const updatePricingLaundryProduct = (product) => {
  if (product?.quantity === 1) return product;

  const { value, original } = product?.pricing || {};

  const priceByQuantity = value ? (value * product?.quantity) : null;
  const originalPriceByQuantity = original
    ? (original * product?.quantity)
    : null;

  if (product?.pricing?.mapDetail?.mapSpecialPriceViolation || product?.pricing?.mapDetail?.mapOriginalPriceViolation) {
    return {
      ...product,
      pricing: {
        ...product?.pricing,
        value: priceByQuantity,
        original: originalPriceByQuantity,
        mapAboveOriginalPrice: false,
        mapDetail: {
          ...product?.pricing?.mapDetail
        }
      }
    };
  }

  const promotion = !!value && !!original && original > value
    ? {
      dollarOff: originalPriceByQuantity - priceByQuantity,
      percentageOff: Math.round(((originalPriceByQuantity - priceByQuantity) / originalPriceByQuantity) * 100)
    }
    : null;

  return {
    ...product,
    pricing: {
      promotion,
      value: (priceByQuantity || originalPriceByQuantity),
      original: originalPriceByQuantity,
      alternatePriceDisplay: true,
      alternate: {
        unit: {
          caseUnitOfMeasure: 'unit',
          unitsOriginalPrice: original,
          unitsPerCase: product?.quantity,
          value: value || original
        }
      },
      unitOfMeasure: 'package'
    }
  };
};